<template>
  <div class="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
    <TopNav @toggle-sidebar="toggleSidebar" />
    <SideMenu :isOpen="isSidebarOpen" @close-sidebar="isSidebarOpen = false" />

    <main class="p-6 md:ml-64 h-auto pt-20 flex justify-center">
      <section class="w-full max-w-lg dark:bg-gray-800 rounded-lg p-8">
        <div class="mb-6 flex justify-between items-center">
          <h2 class="text-3xl font-semibold text-gray-800 dark:text-gray-100">Create LMS Instance</h2>
          <router-link
            :to="$routeWithBranch('LmsInstances')"
            class="mt-4 ml-4 flex items-center text-primary-700 dark:text-primary-300 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span class="ml-2">Back</span>
          </router-link>
        </div>

        <form @submit.prevent="submit" enctype="multipart/form-data">
          <div v-if="errors.length" class="mb-4 bg-red-100 text-red-800 p-3 rounded">
            <ul>
              <li v-for="(error, index) in errors" :key="index" class="text-sm">{{ error }}</li>
            </ul>
          </div>

          <div class="mb-4">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Instance Name</label>
            <input
              type="text"
              v-model="form.name"
              class="w-full p-3 border rounded-lg dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600"
              required
            />
          </div>

          <div class="mb-4">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Domain</label>
            <input
              type="text"
              v-model="form.domain"
              class="w-full p-3 border rounded-lg dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600"
            />
          </div>

          <div class="mb-4">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">License Count</label>
            <input
              type="number"
              v-model.number="form.license_count"
              class="w-full p-3 border rounded-lg dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600"
            />
          </div>

          <div class="mb-4">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Branch Limit</label>
            <input
              type="number"
              v-model.number="form.branch_limit"
              class="w-full p-3 border rounded-lg dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600"
            />
          </div>

          <div class="mb-4">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Theme</label>
            <input
              type="text"
              v-model="form.theme"
              class="w-full p-3 border rounded-lg dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600"
            />
          </div>

          <!-- Logo Upload -->
          <div class="mb-4">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Instance Logo</label>
            <input type="file" @change="handleLogoUpload" accept="image/*" />
            <div v-if="logoPreview" class="relative mt-2 w-32 h-32">
              <img
                :src="logoPreview"
                alt="Logo Preview"
                class="w-full h-full object-cover rounded-md shadow-md"
              />
              <button
                @click.prevent="removeLogo"
                class="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 flex items-center justify-center shadow-lg hover:bg-red-700 transition"
              >
                ✖
              </button>
            </div>
          </div>

          <div class="mb-4">
            <label class="flex items-center space-x-2">
              <input
                type="checkbox"
                v-model="form.api_access"
                class="rounded border-gray-300 dark:bg-gray-700 dark:border-gray-600"
              />
              <span class="text-sm text-gray-700 dark:text-gray-300">API Access Enabled</span>
            </label>
          </div>

          <div class="mb-4">
            <label class="flex items-center space-x-2">
              <input
                type="checkbox"
                v-model="form.branding_enabled"
                class="rounded border-gray-300 dark:bg-gray-700 dark:border-gray-600"
              />
              <span class="text-sm text-gray-700 dark:text-gray-300">Branding Enabled</span>
            </label>
          </div>

          <div class="mb-4">
            <label class="flex items-center space-x-2">
              <input
                type="checkbox"
                v-model="form.is_active"
                class="rounded border-gray-300 dark:bg-gray-700 dark:border-gray-600"
              />
              <span class="text-sm text-gray-700 dark:text-gray-300">Active</span>
            </label>
          </div>

          <div class="mt-6">
            <button
              type="submit"
              :disabled="isSubmitting"
              class="w-full !bg-secondary-600 text-white font-semibold py-3 rounded-lg hover:!bg-secondary-700 flex items-center justify-center"
            >
              <svg
                v-if="isSubmitting"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              {{ isSubmitting ? 'Creating...' : 'Create Instance' }}
            </button>
          </div>
        </form>
      </section>
    </main>
  </div>
</template>

<script>
import TopNav from '@/components/headers/TopNav.vue';
import SideMenu from '@/components/SideMenu.vue';
import axios from 'axios';

export default {
  components: { TopNav, SideMenu },
  data() {
    return {
      form: {
        name: '',
        domain: '',
        theme: '',
        license_count: null,
        branch_limit: null,
        api_access: false,
        branding_enabled: false,
        is_active: true,
      },
      logoFile: null,
      logoPreview: null,
      isSubmitting: false,
      errors: [],
      isSidebarOpen: false,
    };
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.logoFile = file;
        this.logoPreview = URL.createObjectURL(file);
      }
    },
    removeLogo() {
      this.logoFile = null;
      this.logoPreview = null;
    },
    async submit() {
      this.isSubmitting = true;
      this.errors = [];

      const variables = { ...this.form, logo: null };
      const formData = new FormData();

      if (this.logoFile) {
        variables.logo = null; // placeholder for file
        formData.append('map', JSON.stringify({ '1': ['variables.logo'] }));
        formData.append('1', this.logoFile);
      } else {
        formData.append('map', '{}');
      }

      formData.append(
        'operations',
        JSON.stringify({
          query: `
            mutation CreateLmsInstance(
              $name: String!
              $domain: String
              $theme: String
              $license_count: Int
              $branch_limit: Int
              $api_access: Boolean
              $branding_enabled: Boolean
              $is_active: Boolean
              $logo: Upload
            ) {
              createLmsInstance(
                name: $name
                domain: $domain
                theme: $theme
                license_count: $license_count
                branch_limit: $branch_limit
                api_access: $api_access
                branding_enabled: $branding_enabled
                is_active: $is_active
                logo: $logo
              ) {
                id
                name
                logo_url
              }
            }
          `,
          variables,
        })
      );

      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const created = response.data.data.createLmsInstance;
        if (created?.id) {
          this.$routerPushWithBranch('ViewInstance', { params: { id: created.id } });
        }
      } catch (error) {
        console.error('Error creating instance:', error);
        this.errors.push('An error occurred while creating the instance.');
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
